import { FC, PropsWithChildren, useMemo } from "react";
import { Card, CardContent, Grid, TextField, Typography } from "@mui/material";
import { DataGridPro as DataGrid, GridColDef } from "@mui/x-data-grid-pro";

import { get } from "lodash";
import translateLabels from "src/utils/Translations/translateLabels";
import __ from "src/utils/Translations";
import {
  paymentMethodTypeOptions,
  statusesOptions,
  typeOptions,
} from "./constans";
import {
  EventLogsSubscription,
  EventLogsSubscriptionFinancingSources,
} from "./models";

interface Props {
  details: EventLogsSubscription;
}

const Field = ({ label, value }: { label: string; value?: string }) => (
  <TextField
    id={label}
    label={label}
    defaultValue={value || " "}
    slotProps={{ htmlInput: { readOnly: true } }}
    fullWidth
    size="small"
    variant="filled"
  />
);

const SubscriptionBenefitsDetails: FC<PropsWithChildren<Props>> = ({
  details,
  children,
}) => {
  const columns = useMemo<GridColDef<EventLogsSubscriptionFinancingSources>[]>(
    () => [
      {
        headerName: __("ID"),
        field: "id",
        type: "string",
        minWidth: 150,
      },
      {
        headerName: __("Źródło finansowania"),
        field: "type",
        type: "singleSelect",
        valueOptions: translateLabels(typeOptions),
        minWidth: 180,
      },
      {
        headerName: __("Sposób opłacania"),
        field: "paymentMethodType",
        type: "singleSelect",
        valueOptions: translateLabels(paymentMethodTypeOptions),
        minWidth: 180,
      },
      {
        headerName: __("Koszt"),
        field: "cost",
        type: "number",
        valueGetter: (_value, row) => Number(row.cost).toFixed(2),
        minWidth: 120,
      },
      {
        headerName: __("Aktywne od"),
        field: "activeFrom",
        minWidth: 150,
      },
      {
        headerName: __("Aktywne do"),
        field: "activeTo",
        minWidth: 150,
        valueFormatter: (value) => value || __("Czas nieokreślony"),
      },
    ],
    []
  );

  return (
    <>
      <Card variant="outlined">
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={4}>
              <Field
                label={__("Imię")}
                value={get(details, "employee.firstName")}
              />
            </Grid>
            <Grid size={4}>
              <Field
                label={__("Nazwisko")}
                value={get(details, "employee.lastName")}
              />
            </Grid>
            <Grid size={4}>
              <Field
                label={__("Numer pracownika")}
                value={get(details, "employee.fkNumber")}
              />
            </Grid>
            <Grid size={6}>
              <Field label={__("Nazwa produktu")} value={details.productName} />
            </Grid>
            <Grid size={6}>
              <Field
                label={__("E-mail służbowy/prywatny")}
                value={[
                  get(details, "employee.privateEmail"),
                  get(details, "employee.businessEmail"),
                ]
                  .flat()
                  .filter(Boolean)
                  .join(", ")}
              />
            </Grid>
            <Grid size={2}>
              <Field label={__("Data startu")} value={details.startDate} />
            </Grid>
            <Grid size={2}>
              <Field
                label={__("Data rezygnacji")}
                value={details.resignationDate || __("Czas nieokreślony")}
              />
            </Grid>
            <Grid size={8}>
              <Field
                label={__("Powód rezygnacji")}
                value={details.resignationReason}
              />
            </Grid>
            <Grid size={2}>
              <Field
                label={__("Status abonamentu")}
                value={__(
                  statusesOptions.find(({ value }) => value === details.status)
                    ?.label
                )}
              />
            </Grid>
            <Grid size={2}>
              <Field
                label={__("Data anulowania")}
                value={
                  details.canceledAt ? details.canceledAt.slice(0, 10) : ""
                }
              />
            </Grid>
            <Grid size={8}>
              <Field
                label={__("Powód anulowania")}
                value={details.cancellationReason}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      {children}
      <Card variant="outlined" sx={{ my: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ margin: "1rem 0 1rem" }}>
            {__("Źródła finansowania")}
          </Typography>
          <DataGrid
            rows={details?.financingSources || []}
            columns={columns}
            initialState={{
              sorting: {
                sortModel: [{ field: "activeFrom", sort: "desc" }],
              },
            }}
            autosizeOnMount
            disableColumnMenu
            disableEval
            density="compact"
            sx={{
              maxHeight: "300px",
              "& .MuiDataGrid-footerContainer": {
                minHeight: "initial",
              },
            }}
          />
        </CardContent>
      </Card>
    </>
  );
};

export default SubscriptionBenefitsDetails;
